import { getAvailableJobsQuery, JobWithJobType, OFFER_SOURCE, usePerDiemEventTracking } from '@mp';
import { useAnalytics } from '@medely/ui-kit';
import { UseMutateFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePerDiemFiltersContext } from 'context/filters/perDiemFiltersContext';
import { useTrackingQueryParams } from 'hooks/useTrackingQueryParams';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { axios, AxiosResponse } from 'utils/networkRequests';
import useCurrentUser from './useCurrentUser';
import useGraphQLRequest from './useGraphQLRequest';
import useHandleError from './useHandleError';
import useSnackbar from './useSnackbar';

export const DISALLOWED_STATUSES = ['paused', 'paused_requirement_submitted'];

interface IUsePerDiemJobsSearchReturn {
  jobs: JobWithJobType[];
  count: number;
  isLoading: boolean;
  bookJob: UseMutateFunction<AxiosResponse<void>, unknown, number | undefined | null, unknown>;
  bookingLoading: boolean;
  bookJobWithAttestation: UseMutateFunction<
    AxiosResponse<void>,
    unknown,
    { jobId: number; attest: boolean; offer_source: string },
    unknown
  >;
  bookAndAttestLoading: boolean;
}

interface IUsePerDiemJobsSearchReturnParams {
  page?: number;
  perPage?: number;
}

const defaultParams = { page: 1, perPage: 10 };

const usePerDiemJobsSearch = (
  params: IUsePerDiemJobsSearchReturnParams = defaultParams,
): IUsePerDiemJobsSearchReturn => {
  const { page, perPage } = params;
  const { currentUser, isSaas, isW2Eligible, isW2Enabled } = useCurrentUser();
  const navigate = useNavigate();
  const { request } = useGraphQLRequest();
  const queryClient = useQueryClient();

  const { filters, shiftType, distance, isLoading: isLoadingFilters } = usePerDiemFiltersContext();

  const enabled =
    !DISALLOWED_STATUSES.includes(currentUser?.professional?.status ?? '') && !isLoadingFilters;

  const {
    queryParams: { os },
  } = useTrackingQueryParams();
  const offer_source = OFFER_SOURCE[os as keyof typeof OFFER_SOURCE];

  const analytics = useAnalytics();
  const { trackBooked } = usePerDiemEventTracking(analytics);

  const handlePayoutKind = () => {
    if (!isSaas && isW2Eligible && isW2Enabled) {
      if (shiftType?.includes('1099') && shiftType?.includes('W2')) {
        return undefined;
      } else if (shiftType?.includes('W2')) {
        return 'w2';
      } else if (shiftType?.includes('1099')) {
        return 'ten99';
      }
    }
  };

  const queryParamsToGqlInput = () => ({
    search: {
      ...filters,
      starts_time: moment.utc().format(),
      job_type: 'per_diem',
      not_void: true,
      status: 'active',
      distance_check: {
        distance,
        account_id: currentUser?.id,
      },
      payout_kind: handlePayoutKind(),
    },
    perPage,
    page,
  });

  const availabeJobsQuery = getAvailableJobsQuery(currentUser);
  const fetchJobs = async () => {
    const { availableJobs, _availableJobsMeta } = await request(availabeJobsQuery, {
      input: queryParamsToGqlInput(),
      professional_id: currentUser?.professional?.id,
    });

    return { availableJobs, _availableJobsMeta };
  };

  const { data, isLoading, isInitialLoading } = useQuery(
    ['jobs', ...Object.values({ ...filters }), perPage, page, shiftType, distance],
    fetchJobs,
    {
      enabled,
    },
  );

  const handleError = useHandleError();
  const snackbar = useSnackbar();

  const handleBlockedByFavoritesError = (error: any) => {
    if (error.response?.data?.message?.includes('Professional blocked by favorites')) {
      snackbar.default('Job is no longer available');
    } else {
      handleError(error);
    }
  };

  const book = (jobId?: number | null) =>
    axios.post(`/v3/professional/jobs/${jobId}/book`, { offer_source });

  const { mutate: bookJob, isLoading: bookingLoading } = useMutation(book, {
    onSuccess: (response, jobId) => {
      const bookedJobId = jobId ?? response?.data?.payload?.id;
      trackBooked(response?.data?.payload?.id);
      queryClient.invalidateQueries(['GetProfessionalTodos']);

      if (bookedJobId) {
        navigate(`/jobs/${bookedJobId}`, { state: { openPostBookModal: true } });
      }
    },
    onError: (error) => handleBlockedByFavoritesError(error),
    onSettled: () => undefined,
  });

  const bookAndAttest = ({
    jobId,
    attest,
    offer_source,
  }: {
    jobId: number;
    attest: boolean;
    offer_source: string;
  }) => axios.post(`/v3/professional/jobs/${jobId}/book?attest=${attest}`, { offer_source });

  const { mutate: bookJobWithAttestation, isLoading: bookAndAttestLoading } = useMutation(
    bookAndAttest,
    {
      onSuccess: (response, variables) => {
        trackBooked(response?.data?.payload?.id);
        queryClient.invalidateQueries(['GetProfessionalTodos']);

        if (variables?.jobId) {
          navigate(`/jobs/${variables.jobId}`);
        }
      },
      onError: (error) => handleBlockedByFavoritesError(error),
      onSettled: () => undefined,
    },
  );

  return {
    jobs: data?.availableJobs || [],
    count: data?._availableJobsMeta.count || 0,
    isLoading: enabled ? isLoading : isInitialLoading || isLoadingFilters,
    bookJob,
    bookingLoading,
    bookJobWithAttestation,
    bookAndAttestLoading,
  };
};

export default usePerDiemJobsSearch;
